import { useCallback, useMemo } from 'react'
import { graphql } from 'gatsby'
import { RenderRichTextData, ContentfulRichTextGatsbyReference } from 'gatsby-source-contentful/rich-text'
import { BLOCKS, Document, Text } from '@contentful/rich-text-types'
import TabGroup, { TabItem } from '@/components/tab-group'
import { isPrint } from '@/utils/print'
// eslint-disable-next-line import/no-cycle
import Callout from './callout'
// eslint-disable-next-line import/no-cycle
import RichText from './rich-text'

export interface Props {
  groupId: string
  body: RenderRichTextData<ContentfulRichTextGatsbyReference>
}

export const fragment = graphql`
  fragment Tabs on ContentfulComponentTabs {
    __typename
    contentful_id
    body {
      raw
      references {
        ...Asset
        ...Callout
        ...Code
        ...ComponentImage
        ...Icon
        ...Link
        ...Video
      }
    }
  }
`

export default function Tabs({ groupId, body }: Props): JSX.Element {
  const renderCallout = useCallback(
    (ref) => (
      <Callout {...ref} theme="light">
        <RichText body={ref.body} />
      </Callout>
    ),
    []
  )
  const h1Print = useCallback((node, children) => <h2 tw="text-2xl mb-4">⇒{children}</h2>, [])
  const h2 = useCallback((node, children) => <h2 tw="text-[2rem] mb-4">{children}</h2>, [])
  const h3 = useCallback((node, children) => <h3 tw="text-[1.75rem] mb-4">{children}</h3>, [])

  const tabs = useMemo(() => {
    const data = []
    const json = JSON.parse(body.raw) as Document
    json.content.forEach((node) => {
      if (node.nodeType === BLOCKS.HEADING_1) data.push({ heading: node.content as Text[], content: [] })
      else data[data.length - 1].content.push(node)
    })
    return data
  }, [])

  if (isPrint()) return <RichText body={body} h1={h1Print} />

  return (
    <TabGroup>
      {tabs.map((tab, index) => {
        const tabId =
          tab.heading?.[0]?.value?.length > 1 ? tab.heading[0].value.replace(/\s+/g, '-').toLowerCase() : index + 1
        return (
          <TabItem
            tabId={`tab-${tabId}-${groupId}`}
            key={index}
            buttonContent={
              <RichText
                body={{
                  raw: JSON.stringify({
                    nodeType: BLOCKS.DOCUMENT,
                    data: {},
                    content: tab.heading,
                  }),
                  references: body.references,
                }}
              />
            }
          >
            <RichText
              body={{
                raw: JSON.stringify({
                  nodeType: BLOCKS.DOCUMENT,
                  data: {},
                  content: tab.content,
                }),
                references: body.references,
              }}
              h2={h2}
              h3={h3}
              renderCallout={renderCallout}
            />
          </TabItem>
        )
      })}
    </TabGroup>
  )
}
